import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  IconoirCheckCircle,
  IconoirCloudUpload,
  IconoirXmark,
} from '@ey-xd/motif-icon';
import { MotifFormsModule, MotifIconModule } from '@ey-xd/ng-motif';

@Component({
  selector: 'app-custom-motif-uploader',
  standalone: true,
  imports: [MotifIconModule, MotifFormsModule, CommonModule],
  templateUrl: './custom-motif-uploader.component.html',
  styleUrl: './custom-motif-uploader.component.scss',
})
export class CustomMotifUploaderComponent implements OnInit {
  IconoirCloudUpload = IconoirCloudUpload;
  IconoirCheckCircle = IconoirCheckCircle;
  IconoirXmark = IconoirXmark;

  @Input() dropMessage: string = 'Click to upload or drag and drop';
  @Input() acceptZip: boolean = false;
  @Input() acceptDoc: boolean = false;
  @Input() acceptExcel: boolean = false;

  allowedFormatsMessage: string = 'XLSX, XLSM, XLS, XLSB, or CSV (max. 20MB)';

  // this is for the default accepted formats
  acceptedFormats: string = '.xlsx, .xlsm, .csv, .xls, .xlsb';

  MAX_FILE_SIZE: number = 20 * 1024 * 1024; // 20MB

  @Output() fileEmitter = new EventEmitter<File | any>();
  @Output() fileHasError = new EventEmitter<boolean>(false);

  @Input() file: File | any = undefined;
  @Input() reducedWidth: boolean = false;

  hasError: boolean = false;

  activeDescendant: string | null = null;

  onFieldsetFocus() {
    this.activeDescendant = this.file ? 'remove-file-btn' : 'custom-uploader';
  }

  onFieldsetBlur() {
    this.activeDescendant = null; // Clears it to avoid reannouncing
  }

  @ViewChild('customUploaderInput')
  customUploaderInput!: ElementRef<HTMLInputElement>;

  ngOnInit(): void {
    if (this.acceptZip) {
      this.allowedFormatsMessage = 'zip file (max. 20MB)';
      this.acceptedFormats = '.zip';
    }
    if (this.acceptDoc && this.acceptExcel) {
      this.allowedFormatsMessage =
        'XLSX, XLSM, XLS, XLSB or DOC, DOCX (max. 20MB)';
      this.acceptedFormats = '.xlsx, .xlsm, .xls, .xlsb, .doc, .docx';
    }
  }

  triggerFileInput() {
    if (this.customUploaderInput) {
      this.customUploaderInput.nativeElement.click();
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (input?.files && input.files.length > 0) {
      this.handleFile(input.files[0]);
    }
  }

  onFileDrop(event: DragEvent) {
    event.preventDefault();
    const input = event.dataTransfer;

    if (input?.files && input.files.length > 0) {
      this.handleFile(input.files[0]);
    }
  }

  // All the logic that was in one method before is now general to apply to
  // the different file events
  private handleFile(file: File) {
    if (this.hasError) {
      this.hasError = false;
      this.fileHasError.emit(this.hasError);
    }
    const fileExtension = file.name.split('.').pop()?.toLowerCase();

    const isValidType = this.getValidType(file, fileExtension);

    if (isValidType && file.size <= this.MAX_FILE_SIZE) {
      // Handle file upload logic here
      this.file = file;
      this.fileEmitter.emit(file);
    } else {
      this.hasError = true;
      this.fileHasError.emit(this.hasError);
      this.allowedFormatsMessage = this.setAllowedFormatsMessage();
    }
  }

  // Prevents the default behavior during the dragover event to allow the drop event to happen.
  allowDrop(event: DragEvent): void {
    event.preventDefault();
  }

  resetUploader(event?: KeyboardEvent | Event): void {
    if (event) {
      event.stopPropagation(); // Prevent Enter key from bubbling up
    }
    this.file = undefined;
    this.fileEmitter.emit(this.file);
    this.hasError = false;
    this.fileHasError.emit(this.hasError);
    this.customUploaderInput.nativeElement.value = '';
  }

  bytesToMB(sizeInBytes: number): number {
    const sizeInMB = sizeInBytes / (1024 * 1024);
    return parseFloat(sizeInMB.toFixed(3));
  }

  // Private methods to check valid types and set up the messages in each case
  private getValidType(file: File, fileExtension: string | any): boolean {
    if (this.acceptDoc && this.acceptExcel) {
      return (
        // file.type === 'application/zip' ||
        // fileExtension === 'zip' ||
        file.type ===
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        fileExtension === 'docx' ||
        file.type === 'application/msword' ||
        fileExtension === 'doc' ||
        // file.type === 'text/csv' ||
        // fileExtension === 'csv' ||
        file.type ===
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        fileExtension === 'xlsx' ||
        file.type === 'application/vnd.ms-excel.sheet.macroEnabled.12' ||
        fileExtension === 'xlsm' ||
        file.type === 'application/msexcel' ||
        fileExtension === 'xls' ||
        file.type === 'application/vnd.ms-excel.sheet.binary.macroEnabled.12' ||
        fileExtension === 'xlsb'
      );
    }

    if (this.acceptZip) {
      return file.type === 'application/zip' || fileExtension === 'zip';
    } else {
      return (
        file.type === 'text/csv' ||
        fileExtension === 'csv' ||
        file.type ===
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        fileExtension === 'xlsx' ||
        file.type === 'application/vnd.ms-excel.sheet.macroEnabled.12' ||
        fileExtension === 'xlsm' ||
        file.type === 'application/msexcel' ||
        fileExtension === 'xls' ||
        file.type === 'application/vnd.ms-excel.sheet.binary.macroEnabled.12' ||
        fileExtension === 'xlsb'
      );
    }
  }

  private setAllowedFormatsMessage(): string {
    if (this.acceptDoc && this.acceptExcel) {
      return `Select a XLSX, XLSM, XLS, XLSB or DOC, DOCX file under ${
        this.MAX_FILE_SIZE / (1024 * 1024)
      }MB.`;
    }

    if (this.acceptZip) {
      return `Select a zip file under ${this.MAX_FILE_SIZE / (1024 * 1024)}MB.`;
    } else {
      return `Select an XLSX, XLSM, XLS, XLSB or CSV file under ${
        this.MAX_FILE_SIZE / (1024 * 1024)
      }MB.`;
    }
  }
}
